import { useState } from 'react';
import { useComplaints } from '../hooks/useWpApi';
import { SilviaLoader } from '../components/SilviaLoader';
import { RefreshCw, AlertTriangle, Phone, ExternalLink, Filter } from 'lucide-react';
import { ComplaintOrder } from '../services/wordpressApi';
import '../styles/Complaints.css';

const STATUS_FILTERS = [
  { value: '', label: 'Wszystkie' },
  { value: 'wc-processing', label: 'W realizacji' },
  { value: 'wc-completed', label: 'Zrealizowane' },
  { value: 'wc-on-hold', label: 'On-hold' },
];

export function Complaints() {
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const { data, loading, error, refresh, lastRefresh } = useComplaints(statusFilter || undefined, 120_000);

  if (loading && !data) {
    return <SilviaLoader text="Ładowanie reklamacji..." />;
  }

  if (error && !data) {
    return (
      <div className="stats-error">
        <AlertTriangle size={32} />
        <p>{error}</p>
        <button className="btn-refresh" onClick={refresh}>Spróbuj ponownie</button>
      </div>
    );
  }

  if (!data) return null;

  const filteredComplaints = typeFilter
    ? data.complaints.filter(c => c.complaint_type === typeFilter)
    : data.complaints;

  const typeEntries = Object.entries(data.type_counts).sort((a, b) => b[1] - a[1]);

  return (
    <div className="stats-page complaints-page">
      {/* Header */}
      <div className="stats-header">
        <div className="stats-header-top">
          <h1>⚠️ Reklamacje</h1>
          <button className="btn-icon-refresh" onClick={refresh} title="Odśwież">
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          </button>
        </div>
        {lastRefresh && (
          <span className="stats-updated">
            Aktualizacja: {lastRefresh.toLocaleTimeString('pl-PL')}
          </span>
        )}
      </div>

      {/* Summary */}
      <section className="stats-section">
        <div className="complaints-summary">
          <div className="complaints-total-badge">
            <span className="complaints-total-num">{data.total}</span>
            <span className="complaints-total-label">reklamacji</span>
          </div>
        </div>

        {/* Type chips */}
        {typeEntries.length > 0 && (
          <div className="complaints-type-chips">
            <button
              className={`type-chip ${typeFilter === '' ? 'active' : ''}`}
              onClick={() => setTypeFilter('')}
            >
              Wszystkie ({data.total})
            </button>
            {typeEntries.map(([type, count]) => {
              const sample = data.complaints.find(c => c.complaint_type === type);
              return (
                <button
                  key={type}
                  className={`type-chip ${typeFilter === type ? 'active' : ''}`}
                  onClick={() => setTypeFilter(typeFilter === type ? '' : type)}
                >
                  {sample?.complaint_emoji || '❓'} {sample?.complaint_label || type} ({count})
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Filters */}
      <section className="stats-section complaints-filters">
        <div className="filter-row">
          <Filter size={14} />
          <span className="filter-label">Status zamówienia:</span>
          <div className="filter-buttons">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                className={`filter-btn ${statusFilter === f.value ? 'active' : ''}`}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Complaints List */}
      <section className="stats-section">
        {filteredComplaints.length === 0 ? (
          <div className="complaints-empty">
            <span>🎉</span>
            <p>Brak reklamacji{typeFilter ? ' tego typu' : ''}</p>
          </div>
        ) : (
          <div className="complaints-list">
            {filteredComplaints.map(c => (
              <ComplaintCard key={c.order_id} complaint={c} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ComplaintCard({ complaint: c }: { complaint: ComplaintOrder }) {
  const statusLabel = formatStatus(c.order_status);
  const dateStr = new Date(c.date_created).toLocaleDateString('pl-PL', {
    day: 'numeric', month: 'short', year: 'numeric'
  });

  return (
    <div className="complaint-card" style={{ borderLeftColor: c.complaint_color || '#f97316' }}>
      <div className="complaint-header">
        <div className="complaint-type-badge" style={{ background: `${c.complaint_color}20`, color: c.complaint_color }}>
          {c.complaint_emoji} {c.complaint_label}
        </div>
        <a
          href={`/wc-orders/${c.order_id}`}
          className="complaint-order-link"
          title="Szczegóły zamówienia"
        >
          #{c.order_id} <ExternalLink size={12} />
        </a>
      </div>
      <div className="complaint-body">
        <div className="complaint-info-row">
          <span className="complaint-customer">{c.customer_name}</span>
          {c.customer_phone && (
            <a href={`tel:${c.customer_phone}`} className="complaint-phone">
              <Phone size={12} /> {c.customer_phone}
            </a>
          )}
        </div>
        <div className="complaint-meta">
          <span className={`complaint-status ${c.order_status}`}>{statusLabel}</span>
          <span className="complaint-amount">{c.total.toFixed(0)} zł</span>
          <span className="complaint-date">{dateStr}</span>
        </div>
        {c.supplier_order_id && (
          <div className="complaint-supplier">📦 {c.supplier_order_id}</div>
        )}
        {c.order_comments && (
          <div className="complaint-comments">{c.order_comments}</div>
        )}
      </div>
    </div>
  );
}

function formatStatus(status: string): string {
  const map: Record<string, string> = {
    'wc-processing': '🔄 W realizacji',
    'wc-completed': '✅ Zrealizowane',
    'wc-on-hold': '⏸️ On-hold',
    'wc-pending': '⏳ Oczekujące',
    'wc-cancelled': '❌ Anulowane',
    'wc-refunded': '💸 Zwrot',
  };
  return map[status] || status;
}
